import LegDateTime from '@/components/manage/refund/extras/LegDateTime';
import RefundSummary from '@/components/manage/refund/summary/RefundSummary';
import PriceDisplay from '@/components/PriceDisplay';
import LegOriginDestination from '@/components/purchase/checkout/LegOriginDestination';
import {
  manageFlowBookingSelector,
  refundDetailsSelector,
  refundSelectedAncillariesSelector,
} from '@/features/manage/manageSelectors';
import { useBookingRefundEligibleExtras } from '@/hooks/useBookingRefundEligibleExtras';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type {
  BookedAncillaryDTO,
  BookedTripDTO,
  LegDTO,
  PassengerDTO,
} from '@/types/dto/agent';
import { Divider, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, type ReactNode, useMemo } from 'react';

export const RefundExtrasOverview: FC = () => {
  const booking = useSelector(manageFlowBookingSelector);
  const selectedAncillaries = useSelector(refundSelectedAncillariesSelector);
  const refundSummary = useSelector(refundDetailsSelector);
  const {
    ancillariesByLegId,
    extrasSelectionInboundTrips,
    extrasSelectionOutboundTrips,
  } = useBookingRefundEligibleExtras(booking);

  const selectedAncillariesList = useMemo(
    () => Object.values(selectedAncillaries),
    [selectedAncillaries]
  );

  const passengerMap = useMemo(
    () =>
      new Map(
        booking?.passengers?.map((passenger) => [passenger.id, passenger]) ?? []
      ),
    [booking]
  );

  if (!refundSummary || !refundSummary.refundOffers) {
    return null;
  }

  const selectedAncillaryIds = new Set(
    selectedAncillariesList
      .map(({ id }) => id)
      .filter((id): id is string => Boolean(id))
  );

  const tripHasSelectedExtras = (trip: BookedTripDTO) =>
    trip.legs?.some(
      ({ id }) =>
        id &&
        ancillariesByLegId[id]?.some((ancillary) =>
          ancillary.id ? selectedAncillaryIds.has(ancillary.id) : false
        )
    ) ?? false;

  const hasSelectedExtras = [
    ...extrasSelectionOutboundTrips,
    ...extrasSelectionInboundTrips,
  ].some(tripHasSelectedExtras);

  return (
    <div className="p-4">
      <RefundSummary
        refundSummary={refundSummary}
        ancillaries={selectedAncillariesList}
      />
      <Typography variant="subtitle" className="my-5">
        <TransText i18nKey="youAreAboutToRefundFollowingItems" />
      </Typography>
      <div className="flex flex-col gap-3">
        {hasSelectedExtras && (
          <ExtrasSection
            outboundTrips={extrasSelectionOutboundTrips}
            inboundTrips={extrasSelectionInboundTrips}
            title={<TransText i18nKey="selectedExtras" />}
            ancillariesByLegId={ancillariesByLegId}
            selectedAncillaryIds={selectedAncillaryIds}
            passengerMap={passengerMap}
          />
        )}
      </div>
    </div>
  );
};

const AncillaryDetails: FC<{
  ancillary: BookedAncillaryDTO;
  passengerMap: Map<string | undefined, PassengerDTO>;
}> = ({ ancillary, passengerMap }) => {
  const passengerId = ancillary.passengerIds?.[0];
  const passenger = passengerMap.get(passengerId);
  const passengerName =
    `${passenger?.firstName ?? ''} ${passenger?.lastName ?? ''}`.trim();

  return (
    <div className="mt-3 flex flex-row items-stretch justify-between">
      <div className="flex flex-row items-start gap-2">
        <div className="flex h-5 w-5 items-center justify-center">
          <Icons.addon height={16} width={16} className="text-primary" />
        </div>
        <div className="flex flex-col gap-1">
          <Typography variant="body2-bold">
            {ancillary.productDescription || ancillary.productSummary}
          </Typography>
          <Typography variant="body2" className="text-neutral">
            {passengerName}
          </Typography>
        </div>
      </div>
      <Typography variant="body2-bold" className="mt-[2px] text-primary">
        <PriceDisplay amount={ancillary.price?.amount} />
      </Typography>
    </div>
  );
};

const LegExtras: FC<{
  leg: LegDTO;
  ancillariesByLegId: Record<
    NonNullable<LegDTO['id']>,
    Array<BookedAncillaryDTO>
  >;
  selectedAncillaryIds: Set<string>;
  passengerMap: Map<string | undefined, PassengerDTO>;
}> = ({ leg, ancillariesByLegId, selectedAncillaryIds, passengerMap }) => {
  const { id, originStop, destinationStop, ptMode } = leg;
  const selectedLegAncillaries = id
    ? (ancillariesByLegId[id] ?? []).filter(
        (ancillary) => ancillary.id && selectedAncillaryIds.has(ancillary.id)
      )
    : [];

  if (!selectedLegAncillaries.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 px-4 py-3">
      <div className="flex flex-row items-stretch justify-between">
        <LegOriginDestination
          origin={originStop?.name ?? ''}
          destination={destinationStop?.name ?? ''}
          ptMode={ptMode}
        />
        <div className="flex flex-col justify-between">
          <LegDateTime dateTime={leg.departureTime} />
          <LegDateTime dateTime={leg.arrivalTime} />
        </div>
      </div>
      <div className="pl-5">
        {selectedLegAncillaries.map((ancillary) => (
          <AncillaryDetails
            key={ancillary.id}
            ancillary={ancillary}
            passengerMap={passengerMap}
          />
        ))}
      </div>
    </div>
  );
};

const ExtrasSection: FC<{
  outboundTrips: Array<BookedTripDTO>;
  inboundTrips: Array<BookedTripDTO>;
  title: ReactNode;
  ancillariesByLegId: Record<
    NonNullable<LegDTO['id']>,
    Array<BookedAncillaryDTO>
  >;
  selectedAncillaryIds: Set<string>;
  passengerMap: Map<string | undefined, PassengerDTO>;
}> = ({
  outboundTrips,
  inboundTrips,
  title,
  ancillariesByLegId,
  selectedAncillaryIds,
  passengerMap,
}) => {
  const renderLegs = (trips: Array<BookedTripDTO>) =>
    trips.flatMap(
      (trip) =>
        trip.legs?.map((leg) => (
          <LegExtras
            key={leg.id}
            leg={leg}
            ancillariesByLegId={ancillariesByLegId}
            selectedAncillaryIds={selectedAncillaryIds}
            passengerMap={passengerMap}
          />
        )) ?? []
    );

  const hasSelectedLeg = (trips: Array<BookedTripDTO>) =>
    trips.some((trip) =>
      trip.legs?.some(
        ({ id }) =>
          id &&
          ancillariesByLegId[id]?.some((ancillary) =>
            ancillary.id ? selectedAncillaryIds.has(ancillary.id) : false
          )
      )
    );

  const showDivider =
    hasSelectedLeg(outboundTrips) && hasSelectedLeg(inboundTrips);

  return (
    <div className="rounded-md border border-neutral-light">
      <div className="flex items-center justify-between rounded-t-md border-b border-neutral-light bg-primary-pale px-4 py-3">
        <div className="flex flex-row items-center gap-2">
          <Icons.addon height={16} width={16} className="text-primary" />
          <Typography variant="body1-bold">{title}</Typography>
        </div>
      </div>
      {renderLegs(outboundTrips)}
      {showDivider && <Divider className="mx-4 my-2 w-auto" variant="medium" />}
      {renderLegs(inboundTrips)}
    </div>
  );
};

export default RefundExtrasOverview;
